import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Toast } from '../types';

interface UiContextType {
  isCommandPaletteOpen: boolean;
  setIsCommandPaletteOpen: (open: boolean) => void;
  isAiCopilotOpen: boolean;
  setIsAiCopilotOpen: (open: boolean) => void;
  isLicenseActivationOpen: boolean;
  setIsLicenseActivationOpen: (open: boolean) => void;
  toasts: Toast[];
  showToast: (title: string, message: string, type?: 'success' | 'error' | 'info') => void;
  removeToast: (id: string) => void;
}

const UiContext = createContext<UiContextType | undefined>(undefined);

export const UiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isAiCopilotOpen, setIsAiCopilotOpen] = useState(false);
  const [isLicenseActivationOpen, setIsLicenseActivationOpen] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (title: string, message: string, type: 'success' | 'error' | 'info' = 'success') => {
      const id = Math.random().toString(36).slice(2, 9);
      setToasts((prev) => [...prev, { id, title, message, type }]);
      window.setTimeout(() => removeToast(id), 4000);
    },
    [removeToast]
  );

  const value = useMemo(
    () => ({
      isCommandPaletteOpen,
      setIsCommandPaletteOpen,
      isAiCopilotOpen,
      setIsAiCopilotOpen,
      isLicenseActivationOpen,
      setIsLicenseActivationOpen,
      toasts,
      showToast,
      removeToast,
    }),
    [
      isCommandPaletteOpen,
      isAiCopilotOpen,
      isLicenseActivationOpen,
      toasts,
      showToast,
      removeToast,
    ]
  );

  return <UiContext.Provider value={value}>{children}</UiContext.Provider>;
};

export const useUi = () => {
  const ctx = useContext(UiContext);
  if (!ctx) throw new Error('useUi must be used within UiProvider');
  return ctx;
};

/** @deprecated Use useUi — kept as alias during migration */
export const useApp = useUi;
